import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Grid,
  Tooltip,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import EmailIcon from "@mui/icons-material/Email";
import {
  getAuth,
  createUserWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";// fast-path email send
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import EmailVerificationWaiting from "./EmailVerificationWaiting";
import { ReactComponent as GoogleLogo } from "../assets/google.svg";

// IMPORT YOUR POLICY COMPONENT (filename you showed)
import PrivacyPolicy from "./PrivatePolicy";

/* Minimal in-app browser banner (unchanged) */
function InAppBrowserBanner({ show }) {
  if (!show) return null;

  const ua = (navigator.userAgent || "").toLowerCase();
  const isAndroid = /android/i.test(ua);
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const browserLabel = isIOS ? "Safari" : "Chrome";

  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      const tmp = document.createElement("input");
      tmp.value = window.location.href;
      document.body.appendChild(tmp);
      tmp.select();
      document.execCommand("copy");
      document.body.removeChild(tmp);
    } finally {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const openInChromeAndroid = () => {
    const url = window.location.href;
    const intent = `intent://${window.location.host}${window.location.pathname}${window.location.search}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(
      url
    )};end`;
    window.location.href = intent;
  };

  return (
    <Box
      role="region"
      aria-label="Open in browser instructions"
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: "#000",
        color: "#fff",
        px: 2,
        py: 1.5,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        borderTop: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, lineHeight: 1.1, mb: 0.25, color: "#fff" }}
        >
          Finish Sign-Up
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.25, pr: 1, color: "#fff" }}>
          Google Sign-Up isn’t supported inside in-app browsers.{" "}
          <strong>Open in {browserLabel} now to finish instantly.</strong>
        </Typography>
      </Box>

      <Button
        onClick={copyLink}
        size="small"
        variant="contained"
        disabled={copied}
        sx={{
          bgcolor: "#fff",
          color: "#000",
          fontWeight: 700,
          textTransform: "none",
          boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
          "&:hover": { bgcolor: "#f2f2f2" },
          "&:active": { transform: "scale(0.98)" },
          transition: "transform .06s ease",
        }}
        aria-live="polite"
      >
        {copied ? "Copied!" : "Copy link"}
      </Button>

      {isAndroid && (
        <Button
          onClick={openInChromeAndroid}
          size="small"
          variant="contained"
          sx={{
            bgcolor: "#fff",
            color: "#000",
            fontWeight: 700,
            textTransform: "none",
            "&:hover": { bgcolor: "#f2f2f2" },
          }}
        >
          Open in Chrome
        </Button>
      )}
    </Box>
  );
}

const SignUp = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isVerifying, setIsVerifying] = useState(
    localStorage.getItem("waitingForVerification") === "true"
  );
  const [googleLoading, setGoogleLoading] = useState(false);

  // NEW: modal state for policy
  const [openPolicy, setOpenPolicy] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const u = getAuth().currentUser;
    if (u?.emailVerified) navigate("/create-event", { replace: true });
  }, [navigate]);

  // Detect in-app browser
  const [inApp, setInApp] = useState(false);
  // controls the black bottom banner visibility
  const [showBanner, setShowBanner] = useState(true);

  useEffect(() => {
    const ua = (navigator.userAgent || "").toLowerCase();
    const hay = ua + " " + (document.referrer || "").toLowerCase();
    const detected =
      /(instagram|fb_iab|fbav|facebook|messenger|line|micromessenger|wechat|weibo|qq|snapchat|pinterest|tiktok|bytedance|musical\.ly|lemon8|ttwebview|twitter)/i.test(
        hay
      );
    setInApp(detected);
  }, []);

  // Clear stale waiting flag if provider != password
  useEffect(() => {
    const u = getAuth().currentUser;
    const isPasswordUser = u?.providerData?.some((p) => p.providerId === "password");
    if (u && !isPasswordUser) {
      localStorage.removeItem("waitingForVerification");
      setIsVerifying(false);
    }
  }, []);

  // GOOGLE
  const handleGoogleSignUp = async () => {
    if (inApp || googleLoading) return;
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    try {
      setGoogleLoading(true);

      const { user } = await signInWithPopup(auth, provider);

      localStorage.removeItem("waitingForVerification");
      setIsVerifying(false);

      await setDoc(
        doc(db, "users", user.uid),
        {
          email: user.email,
          eventCount: 0,
          stripeId: "",
          subscriptionStatus: "free_trial",
          signupProvider: "google.com",
          emailVerifiedAt: serverTimestamp(), // Google accounts come verified
        },
        { merge: true }
      );

      navigate("/create-event", { replace: true });
    } catch (err) {
      console.error("Google signup failed:", err);
      setGoogleLoading(false);
      alert("Google signup error: " + err.message);
    }
  };

  // APPLE
  // For first pass, we do NOT request name/email,
  // so Apple lets us use response_mode=query and redirect with ?code=...
  const APPLE_REDIRECT_URI = "https://elevateevent.co/auth/callback/apple";
  const APPLE_CLIENT_ID = "co.elevateevent.web";
  const APPLE_SCOPE = ""; // <— IMPORTANT: no "name email" here
  const APPLE_RESPONSE_TYPE = "code";
  const APPLE_RESPONSE_MODE = "query";

  function randomState() {
    return Math.random().toString(36).slice(2);
  }

  const handleAppleStart = () => {
    const state = randomState();
    const url = new URL("https://appleid.apple.com/auth/authorize");

    url.searchParams.set("response_type", APPLE_RESPONSE_TYPE);
    url.searchParams.set("response_mode", APPLE_RESPONSE_MODE);
    url.searchParams.set("client_id", APPLE_CLIENT_ID);
    url.searchParams.set("redirect_uri", APPLE_REDIRECT_URI);

    if (APPLE_SCOPE) {
      // only include scope if it's non-empty
      url.searchParams.set("scope", APPLE_SCOPE);
    }

    url.searchParams.set("state", state);

    sessionStorage.setItem("apple_oauth_state", state);

    window.location.href = url.toString();
  };

  // EMAIL/PASSWORD
  const handleSignUp = async (e) => {
    e.preventDefault();
    if (isVerifying) return;

    try {
      const auth = getAuth();
      await setPersistence(auth, browserLocalPersistence);

      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      if (!user?.uid) throw new Error("Error: User UID is missing.");

      // flip UI immediately
      localStorage.setItem("pendingEmail", email);
      localStorage.setItem("waitingForVerification", "true");
      setIsVerifying(true);

    

      await setDoc(
        doc(db, "users", user.uid),
        {
          email: user.email,
          eventCount: 0,
          stripeId: "",
          subscriptionStatus: "free_trial",
          signupProvider: "password",
        },
        { merge: true }
      );
    } catch (error) {
      console.error("Signup failed:", error);
      let message = "Something went wrong. Please try again.";

      if (error.code === "auth/email-already-in-use") {
        message = "This email already has an account. Please sign in instead.";
      } else if (error.code === "auth/invalid-email") {
        message = "Please enter a valid email address.";
      } else if (error.code === "auth/weak-password") {
        message = "Please use a stronger password.";
      }

      alert(message);
      localStorage.removeItem("waitingForVerification");
      setIsVerifying(false);
    }
  };

  if (googleLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          px: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Creating your event dashboard...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          One second — we’re getting everything ready.
        </Typography>
      </Box>
    );
  }

  if (isVerifying) return <EmailVerificationWaiting />;

  return (
    <>
      <Container
        maxWidth="sm"
        sx={{ marginTop: { xs: "80px", md: "100px" }, paddingBottom: "20px" }}
      >
        <Paper elevation={3} sx={{ p: 2.5, mt: "50px" }}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography variant="h4" component="h1" gutterBottom>
              Sign Up
            </Typography>

            <form onSubmit={handleSignUp} style={{ width: "100%" }}>
              <Grid container spacing={2}>
                {/* Google */}
                <Grid item xs={12}>
                  <Box textAlign="center" mb={1}>
                    <Box
                      onClick={handleGoogleSignUp}
                      aria-label="Sign up with Google"
                      sx={{
                        display: "inline-block",
                        cursor: inApp || googleLoading ? "not-allowed" : "pointer",
                        opacity: inApp || googleLoading ? 0.4 : 1,
                        transition: "opacity .2s ease",
                        pointerEvents: inApp || googleLoading ? "none" : "auto",
                      }}
                    >
                      <GoogleLogo />
                    </Box>
                  </Box>
                </Grid>

                {/* Apple */}
                <Grid item xs={12}>
                  <Box textAlign="center" mb={1}>
                    <Button
                      type="button"
                      onClick={handleAppleStart}
                      aria-label="Sign up with Apple"
                      sx={{
                        backgroundColor: "#000",
                        color: "#fff",
                        borderRadius: "999px",
                        px: 2.5,
                        py: 1.2,
                        fontWeight: 600,
                        textTransform: "none",
                        minWidth: 260,
                        fontSize: "0.9rem",
                        boxShadow: "0 4px 10px rgba(0,0,0,0.4)",
                        "&:hover": {
                          backgroundColor: "#111",
                          boxShadow: "0 6px 14px rgba(0,0,0,0.5)",
                        },
                      }}
                    >
                      {/* using the Apple glyph directly is ok for now */}
                      <span style={{ fontSize: "1rem", marginRight: "8px" }}></span>
                      Continue with Apple
                    </Button>
                  </Box>
                </Grid>

                {/* OR divider */}
                <Grid item xs={12}>
                  <Divider>or</Divider>
                </Grid>

                {/* “Sign up with Email” pill to reveal fields */}
                <Grid item xs={12}>
                  {!showEmailForm && (
                    <Box textAlign="center" mb={1}>
                      <Button
                        variant="contained"
                        onClick={() => {
                          setShowEmailForm(true);
                          setShowBanner(false); // NEW: hide the bottom banner once they pick email
                        }}
                        startIcon={<EmailIcon sx={{ color: "#202124" }} />}
                        sx={{
                          bgcolor: "#fff",
                          color: "#202124",
                          textTransform: "none",
                          fontWeight: 500,
                          fontSize: "0.9rem",
                          borderRadius: "999px",
                          border: "1px solid #dadce0",
                          height: 44,
                          minWidth: 260,
                          px: 2.5,
                          boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                          "&:hover": {
                            bgcolor: "#f8f9fa",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                          },
                        }}
                        aria-label="Sign up with Email"
                      >
                        Sign up with Email
                      </Button>
                    </Box>
                  )}
                </Grid>

                {/* Subtle Terms/Privacy under the email section (opens modal / links) */}
                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    align="center"
                    sx={{ display: "block", color: "text.secondary", mt: 0.5 }}
                  >
                    By proceeding, you agree to the{" "}
                    <Link
                      to="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms and Conditions
                    </Link>{" "}
                    and{" "}
                    <Link
                      to="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </Typography>
                </Grid>

                {/* Email/password fields */}
                {showEmailForm && (
                  <>
                    <Grid item xs={12}>
                      <Tooltip title="Enter a valid email address" arrow>
                        <TextField
                          label="Email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          fullWidth
                          required
                          variant="outlined"
                          inputProps={{ maxLength: 254 }}
                          InputProps={{
                            endAdornment: (
                              <IconButton>
                                <InfoIcon />
                              </IconButton>
                            ),
                          }}
                        />
                      </Tooltip>
                    </Grid>

                    <Grid item xs={12}>
                      <Tooltip
                        title="Use at least 8 characters with a number and a special character"
                        arrow
                      >
                        <TextField
                          label="Password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          fullWidth
                          required
                          variant="outlined"
                          inputProps={{ maxLength: 128 }}
                          InputProps={{
                            endAdornment: (
                              <IconButton>
                                <InfoIcon />
                              </IconButton>
                            ),
                          }}
                        />
                      </Tooltip>
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        sx={{
                          background:
                            "linear-gradient(90deg, #2A73E8 0%, #1A5BBE 100%)",
                          color: "#fff",
                          fontWeight: "bold",
                          fontSize: "16px",
                          textTransform: "uppercase",
                          p: "12px 16px",
                          borderRadius: "8px",
                          "&:hover": {
                            background:
                              "linear-gradient(90deg, #1A5BBE 0%, #12459A 100%)",
                            boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                          },
                        }}
                      >
                        Sign Up
                      </Button>
                    </Grid>
                  </>
                )}
              </Grid>
            </form>
          </Box>
        </Paper>
      </Container>

      {/* Privacy Policy modal */}
      <Dialog
        open={openPolicy}
        onClose={() => setOpenPolicy(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pr: 5 }}>
          Privacy Policy
          <IconButton
            aria-label="Close"
            onClick={() => setOpenPolicy(false)}
            sx={{ position: "absolute", right: 12, top: 8 }}
          >
            ✕
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1 }}>
          <PrivacyPolicy />
        </DialogContent>
      </Dialog>

      {/* Minimal bottom banner only when inside in-app browsers */}
      <InAppBrowserBanner show={inApp && showBanner} />
    </>
  );
};

export default SignUp;